import { useCallback, useMemo, useState } from 'react';
import { DEFAULT_PAGE_SIZE, pageOffset } from '@forecastify/shared';
import type { PagerState } from '@forecastify/ui';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Which page of how many — an offset, a total, and the arithmetic once.     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THIS WAS A CURSOR STACK AND IS NOW AN OFFSET, AND THE FEEDBACK OVERRULED THE RULE THAT
 * MADE IT A STACK.** `state-rules.md` §6 said *never page numbers over a keyset cursor*, and the
 * note that stood here explained the stack it forced: a keyset only walks forward, so *previous*
 * was re-fetching with a cursor the client had kept. Item 20 of the 9 Sep 2026 feedback asks for
 * `[<] [selector] [>] [total]` — a control somebody can jump through — and **a cursor cannot answer
 * "what comes after 300 rows I have not read"** at any price. So the server counts the filtered set
 * and the client sends an offset; `pagination.ts` records what that cost.
 *
 * ⚠️ **The drift an offset has is real and is the accepted cost.** Insert a row while somebody is
 * on page two and one row repeats; delete one and one is skipped. That is what every offset pager
 * in every product does, and the alternative on offer was no page numbers at all.
 *
 * ⚠️⚠️ **CHANGING A FILTER MUST STILL RESET IT, and forgetting to is how a list shows an empty
 * page.** The trap moved rather than went away: offset 400 in a set that a filter has cut to twelve
 * rows is past the end, and the screen renders nothing with no error to explain it. `reset()` is
 * called from the same place the filter changes.
 */

export interface Pages {
  /** How many rows to skip. Goes on the wire as `?offset=`. */
  readonly offset: number;
  /** How many rows a page holds. Goes on the wire as `?limit=`. */
  readonly limit: number;
  /** ⚠️ ZERO-BASED. The control adds the one — `pageLabel` below is the only place that does. */
  readonly index: number;
  /** ⚠️ At least 1, even when the list is empty: *page 1 of 1, showing nothing* is a true sentence
      and *page 1 of 0* is not. */
  readonly count: number;
  /** The filtered total the last page came back with. */
  readonly total: number;
  readonly hasPrevious: boolean;
  readonly hasNext: boolean;
  readonly next: () => void;
  readonly previous: () => void;
  /** ⚠️ Zero-based, and clamped — a selector cannot ask for a page that is not there. */
  readonly goTo: (index: number) => void;
  /** ⚠️ Back to the first page. Call whenever the SET changes, not just when the sort does. */
  readonly reset: () => void;
  /** Record the `meta.total` the page that has just arrived came back with. */
  readonly settle: (total: number) => void;
}

export function usePages(limit: number = DEFAULT_PAGE_SIZE): Pages {
  const [index, setIndex] = useState(0);
  const [total, setTotal] = useState(0);

  const count = Math.max(1, Math.ceil(total / limit));

  const goTo = useCallback(
    (to: number) => {
      /* ⚠️ Clamped against the page count computed HERE rather than read from the closure, so a
         jump made in the same tick as an arriving total cannot land past the end. */
      const pages = Math.max(1, Math.ceil(total / limit));
      setIndex(Math.min(Math.max(0, to), pages - 1));
    },
    [total, limit],
  );

  const next = useCallback(() => {
    setIndex((current) => {
      const pages = Math.max(1, Math.ceil(total / limit));
      return Math.min(current + 1, pages - 1);
    });
  }, [total, limit]);

  const previous = useCallback(() => {
    setIndex((current) => Math.max(0, current - 1));
  }, []);

  const reset = useCallback(() => {
    setIndex(0);
    /**
     * ⚠️ **The total is NOT cleared.** Clearing it would collapse the pager to *1 of 1* for the one
     * render between the filter changing and its page arriving, which reads as *the filter matched
     * nothing* — the answer the screen is about to replace. The stale figure is wrong for a frame;
     * the cleared one is wrong in a way somebody acts on.
     */
  }, []);

  return useMemo(
    () => ({
      offset: pageOffset(index * limit),
      limit,
      index,
      count,
      total,
      hasPrevious: index > 0,
      hasNext: index + 1 < count,
      next,
      previous,
      goTo,
      reset,
      settle: setTotal,
    }),
    [index, limit, count, total, next, previous, goTo, reset],
  );
}

/** 31 Oct 2026 · what `DataList`'s `page` takes, from a server pager — the adapter once. */
export function pagerOf(pages: Pages): PagerState {
  return {
    hasPrevious: pages.hasPrevious,
    hasNext: pages.hasNext,
    onPrevious: pages.previous,
    onNext: pages.next,
    index: pages.index,
    count: pages.count,
    total: pages.total,
    onGoTo: pages.goTo,
  };
}
